import { Box, Button, Container, Spinner, Stack, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useSearch } from "@tanstack/react-router"
import { useEffect } from "react"

import { QuizzesService } from "@/client"
import type { QuizPublic } from "@/client"
import { PrintableQuiz } from "@/components/Export/PrintableQuiz"
import { useBranding } from "@/hooks/useBranding"

export const Route = createFileRoute("/_layout/quiz-management/$quizId/print")({
  component: QuizPrintPage,
})

function QuizPrintPage() {
  const { quizId } = Route.useParams()
  const search = useSearch({ from: "/_layout/quiz-management/$quizId/print" })
  const { display_name } = useBranding()

  const includeAnswers = search.answers === "1"
  const includeRationales = search.rationales === "1"
  const twoColumns = search.columns !== "1"

  const {
    data: quiz,
    isLoading,
    error,
  } = useQuery<QuizPublic>({
    queryKey: ["quiz", quizId],
    queryFn: () => QuizzesService.getQuiz({ quizId }),
  })

  // Set document title for PDF export filename
  useEffect(() => {
    if (quiz?.name) {
      document.title = quiz.name
    }
    return () => {
      document.title = display_name
    }
  }, [quiz?.name, display_name])

  if (isLoading) {
    return (
      <Container maxW="6xl" py={10} data-testid="quiz-print-page">
        <Stack align="center" py={20}>
          <Spinner size="lg" />
        </Stack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxW="6xl" py={10} data-testid="quiz-print-page">
        <Box
          role="alert"
          borderWidth="1px"
          borderRadius="md"
          borderColor="red.200"
          bg="red.50"
          p={4}
        >
          <Text fontWeight="semibold" color="red.700" mb={1}>
            Unable to load quiz
          </Text>
          <Text color="red.700">
            {(error as Error).message || "Please try again later."}
          </Text>
        </Box>
      </Container>
    )
  }

  if (!quiz) {
    return (
      <Container maxW="6xl" py={10} data-testid="quiz-print-page">
        <Box
          role="alert"
          borderWidth="1px"
          borderRadius="md"
          borderColor="yellow.200"
          bg="yellow.50"
          p={4}
        >
          <Text fontWeight="semibold" color="yellow.700">
            No quiz data available.
          </Text>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxW="6xl" py={10} data-testid="quiz-print-page">
      <Stack mb={4} direction="row" className="no-print">
        <Button
          colorPalette="blue"
          data-testid="quiz-print-now"
          onClick={() => window.print()}
        >
          Print
        </Button>
      </Stack>
      <PrintableQuiz
        quiz={{
          id: quiz.id,
          name: quiz.name,
          description: null,
          questions: (quiz.questions ?? []).map((question, index) => ({
            id: question.question_id,
            order:
              typeof question.order === "number" ? question.order : index + 1,
            questionText: question.question_text,
            options: question.options,
            correctAnswerIndex: question.correct_answer ?? null,
            rationale: question.rationale ?? null,
          })),
        }}
        options={{
          includeAnswers,
          includeRationales,
          twoColumns,
        }}
      />
    </Container>
  )
}
